"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useRef, type PointerEvent } from "react";
import { CarouselContext } from "./CarouselContext";

/** Distance before we lock into a horizontal swipe (claim the gesture). */
const LOCK_THRESHOLD_PX = 12;
/** Distance to commit a slide change on pointer up. */
const SWIPE_THRESHOLD_PX = 40;
/**
 * Horizontal vs vertical ratio required to lock a swipe.
 * 1 means dx must be at least as large as dy — allows mild diagonals.
 */
const HORIZONTAL_DOMINANCE = 1;
type SwipeStart = {
  x: number;
  y: number;
  pointerId: number;
  locked: boolean;
};
export function useCarouselSwipe() {
  const $ = _c(10);
  const {
    goToPrevious,
    goToNext
  } = useContext(CarouselContext);
  const startRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const activePointersRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = target => {
      const start = startRef.current;
      startRef.current = null;
      if (start?.locked && target.hasPointerCapture(start.pointerId)) {
        target.releasePointerCapture(start.pointerId);
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clearSwipe = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (event.pointerType === "mouse") {
        return;
      }
      activePointersRef.current.add(event.pointerId);
      if (activePointersRef.current.size > 1) {
        clearSwipe(event.currentTarget);
        return;
      }
      startRef.current = {
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId,
        locked: false
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onPointerDown = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event_0 => {
      const start_0 = startRef.current;
      if (!start_0 || start_0.locked || event_0.pointerType === "mouse" || event_0.pointerId !== start_0.pointerId || activePointersRef.current.size > 1) {
        return;
      }
      const dx = event_0.clientX - start_0.x;
      const dy = event_0.clientY - start_0.y;
      if (Math.abs(dx) < LOCK_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy) * HORIZONTAL_DOMINANCE) {
        return;
      }
      event_0.currentTarget.setPointerCapture(event_0.pointerId);
      start_0.locked = true;
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onPointerMove = t3;
  let t4;
  if ($[4] !== goToNext || $[5] !== goToPrevious) {
    t4 = event_1 => {
      activePointersRef.current.delete(event_1.pointerId);
      const start_1 = startRef.current;
      if (!start_1 || event_1.pointerType === "mouse" || event_1.pointerId !== start_1.pointerId) {
        return;
      }
      const wasLocked = start_1.locked;
      const startX = start_1.x;
      clearSwipe(event_1.currentTarget);
      if (!wasLocked || activePointersRef.current.size > 0) {
        return;
      }
      const dx_0 = event_1.clientX - startX;
      if (Math.abs(dx_0) < SWIPE_THRESHOLD_PX) {
        return;
      }
      if (dx_0 < 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    };
    $[4] = goToNext;
    $[5] = goToPrevious;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onPointerUp = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event_2 => {
      activePointersRef.current.delete(event_2.pointerId);
      clearSwipe(event_2.currentTarget);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onPointerCancel = t5;
  let t6;
  if ($[8] !== onPointerUp) {
    t6 = {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel
    };
    $[8] = onPointerUp;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}